import React from 'react';
import InfoIcon from '../icons/InfoIcon';
import styles from './CmsNoSearchResultsLinks.module.css';

// --- Types ---

interface CmsFile {
  url: string;
  description?: string;
}

interface CmsGroupItem {
  label: string;
  body: string;
  image: CmsFile | null;
  url: string;
  external: boolean;
  _metadata: { uid: string };
}

interface CmsGroup {
  group_title: string;
  group_subtitle: string;
  group_icon: CmsFile | null;
  items: CmsGroupItem[];
  _metadata: { uid: string };
}

interface CmsNoSearchResultsLinksData {
  name?: string;
  body?: string;
  component?: string;
  groups?: CmsGroup[];
}

interface CmsNoSearchResultsLinksProps {
  data?: CmsNoSearchResultsLinksData;
}

interface ParsedBody {
  productItems: string[];
  price: string;
  label: string;
  sublabel: string;
  tooltipHeadline: string;
}

// --- Constants ---

const LIGA_OFF = { fontFeatureSettings: "'liga' off" };
const CARD_SHADOW = 'shadow-[0_3px_4px_0_rgba(0,0,0,0.15)]';
const TABLE_CARD_VARIANT = '4';

// --- Helpers ---

const extractAll = (html: string, regex: RegExp): string[] => {
  const results: string[] = [];
  let match;
  while ((match = regex.exec(html)) !== null) {
    const text = match[1].trim();
    if (text) results.push(text);
  }
  return results;
};

const parseItemBody = (body: string): ParsedBody => {
  const productItems = extractAll(body, /<h4\b[^>]*>(.*?)<\/?h4>/gi);
  const price = /<p\b[^>]*>(.*?)<\/?p>/gi.exec(body)?.[1]?.trim() ?? '';
  const h5Matches = extractAll(body, /<h5\b[^>]*>(.*?)<\/?\w+>/gi);
  const label = h5Matches[0] ?? '';
  const sublabel = h5Matches[1] ?? /<h6\b[^>]*>(.*?)<\/?\w+>/gi.exec(body)?.[1]?.trim() ?? '';
  const tooltipHeadline = /<h3\b[^>]*>(.*?)<\/?h3>/gi.exec(body)?.[1]?.trim() ?? '';
  return { productItems, price, label, sublabel, tooltipHeadline };
};

// --- Shared sub-components ---

const PercentageBadge: React.FC<{ value: string }> = ({ value }) => (
  <div className="flex items-start flex-shrink-0">
    <span
      className="text-[100px] font-bold text-[#22539F] leading-heading tracking-[-3px]"
      style={LIGA_OFF}
    >
      {value}
    </span>
    <div className="flex flex-col items-start self-start ml-[2px]">
      <span
        className="text-[55px] font-bold text-[#22539F] leading-heading tracking-[-1.65px] mt-[10px]"
        style={LIGA_OFF}
      >
        %
      </span>
      <span
        className="text-body-125 font-bold text-[#2253A0] leading-heading tracking-[-0.3px]"
        style={LIGA_OFF}
      >
        OFF
      </span>
    </div>
  </div>
);

const CardImage: React.FC<{
  image: CmsFile | null;
  className?: string;
  imgClassName: string;
}> = ({ image, className = '', imgClassName }) => (
  <div className={`flex-shrink-0 ${className}`}>
    {image ? (
      <img
        src={image.url}
        alt={image.description ?? ''}
        className={`${imgClassName} object-contain`}
      />
    ) : (
      <div
        className={`flex items-center justify-center font-bold ${imgClassName} text-[32px] text-[#999]`}
      >
        FPO
      </div>
    )}
  </div>
);

const BulletList: React.FC<{ items: string[]; compact?: boolean }> = ({
  items,
  compact = false,
}) => (
  <ul
    className={`list-disc pl-125 m-0 flex flex-col gap-[12px] ${compact ? 'max-w-[137px] pt-[23px]' : 'w-[179px]'}`}
  >
    {items.map((name) => (
      <li
        key={name}
        className="font-bold text-black text-body-100 leading-[112.5%] tracking-[-0.48px]"
        style={LIGA_OFF}
      >
        {name}
      </li>
    ))}
  </ul>
);

const PriceText: React.FC<{ price: string; className?: string }> = ({ price, className = '' }) => (
  <span
    className={`font-semibold text-[#0054A4] text-detail-90 leading-[112.5%] tracking-[-0.42px] ${className}`}
    style={LIGA_OFF}
  >
    {price}
  </span>
);

const InfoTooltip: React.FC<{ headline: string }> = ({ headline }) => (
  <div className="relative group inline-flex">
    <InfoIcon fill="#22539F" width={24} height={24} className="cursor-pointer" />
    <div className="absolute z-10 hidden group-hover:block w-[358px] right-[-30px] bottom-[-84px]">
      <div className="bg-gray-675 rounded-200 p-100 text-white flex flex-col items-start gap-[10px] shadow-100">
        {headline && <p className="font-bold text-body-100 m-0">{headline}</p>}
        <span className="text-body-100 font-normal opacity-70">List the SKUs here...</span>
      </div>
    </div>
  </div>
);

const GroupBanner: React.FC<{
  title: string;
  subtitle?: string;
  className?: string;
  compact?: boolean;
}> = ({ title, subtitle, className = '', compact = false }) => (
  <div className={`bg-white rounded-[15px] p-[8px] ${CARD_SHADOW} ${className}`}>
    <div
      className={`flex flex-col justify-center items-center bg-[#22539F] rounded-[10px] ${compact ? 'py-[4px] px-[8px] gap-[2px]' : 'py-60 px-50 gap-25'}`}
    >
      <span
        className="text-white text-center font-bold text-[24px] leading-[112.5%] tracking-[-0.72px]"
        style={LIGA_OFF}
      >
        {title}
      </span>
      {subtitle && (
        <span
          className="text-[#C0E1F5] text-center font-semibold text-[14px] leading-[112.5%] tracking-[-0.42px]"
          style={LIGA_OFF}
        >
          {subtitle}
        </span>
      )}
    </div>
  </div>
);

// --- Table card (tier-e: url === "4") ---

const TableCard: React.FC<{ item: CmsGroupItem }> = ({ item }) => (
  <div
    className={`w-full bg-white rounded-[20px] p-[20px] flex items-end gap-[14px] ${CARD_SHADOW}`}
  >
    <CardImage
      image={item.image}
      className="self-end"
      imgClassName="w-[367px] h-[288px] object-bottom"
    />
    {item.body && (
      <div className="flex-1 overflow-hidden self-start border border-[#13356F] rounded-lg bg-[#EDF2F8]">
        <div className={styles.tableCardWrapper} dangerouslySetInnerHTML={{ __html: item.body }} />
      </div>
    )}
  </div>
);

// --- Card components ---

const ProductCard: React.FC<{ item: CmsGroupItem }> = ({ item }) => {
  const { productItems, price } = parseItemBody(item.body);
  return (
    <div
      className={`flex-1 flex items-center h-[194px] rounded-[15px] bg-white ${CARD_SHADOW} overflow-visible`}
    >
      <div className="pl-[30px]">
        <PercentageBadge value={item.label} />
      </div>
      <CardImage
        image={item.image}
        className="self-end w-[214px] relative"
        imgClassName="absolute bottom-[13px] right-0 w-[214px] h-auto max-h-[220px]"
      />
      <div className="flex flex-col justify-center gap-[25px]">
        {productItems.length > 0 && <BulletList items={productItems} />}
        {price && <PriceText price={price} />}
      </div>
    </div>
  );
};

const VerticalProductCard: React.FC<{ item: CmsGroupItem }> = ({ item }) => {
  const { productItems, price, label, tooltipHeadline } = parseItemBody(item.body);
  const hasProducts = productItems.length > 0;
  const hasTooltip = !hasProducts && !!tooltipHeadline;

  return (
    <div
      className={`flex-1 flex flex-col rounded-[20px] bg-white ${CARD_SHADOW} p-[20px] min-h-[310px]`}
    >
      <div className="flex justify-center">
        <PercentageBadge value={item.label} />
      </div>
      {label && (
        <span
          className="font-bold text-center text-[#22539F] text-body-100 leading-heading tracking-[-0.48px] mb-[10px] inline-flex items-center justify-center gap-[2px]"
          style={LIGA_OFF}
        >
          {label}
          {hasTooltip && <InfoTooltip headline={tooltipHeadline} />}
        </span>
      )}
      {hasProducts ? (
        <div className="flex items-start gap-[10px] flex-1">
          <CardImage image={item.image} className="ml-[34px]" imgClassName="w-[149px] h-[149px]" />
          <BulletList items={productItems} compact />
        </div>
      ) : (
        <div className="flex flex-col items-center flex-1">
          <CardImage image={item.image} imgClassName="w-[149px] h-[149px]" />
        </div>
      )}
      {price && <PriceText price={price} className="mt-[10px] text-center" />}
    </div>
  );
};

const LabelCard: React.FC<{ item: CmsGroupItem }> = ({ item }) => {
  const { label, sublabel } = parseItemBody(item.body);
  return (
    <div className={`flex-1 flex rounded-[15px] bg-white ${CARD_SHADOW} overflow-hidden`}>
      <div className="flex flex-col items-center flex-1 pt-[5px]">
        <PercentageBadge value={item.label} />
        {label && (
          <span
            className="font-bold text-center text-[#22539F] text-body-125 leading-heading tracking-[-0.6px]"
            style={LIGA_OFF}
          >
            {label}
          </span>
        )}
        {sublabel && (
          <span
            className="font-medium text-center text-[#789ED9] text-detail-90 leading-heading tracking-[-0.42px] mt-25"
            style={LIGA_OFF}
          >
            {sublabel}
          </span>
        )}
      </div>
      <CardImage
        image={item.image}
        className="py-[19px] pr-[71px]"
        imgClassName="w-[199px] h-[156px]"
      />
    </div>
  );
};

// --- Card renderer ---

const renderCard = (item: CmsGroupItem, variant: 'horizontal' | 'vertical') => {
  if (item.url === TABLE_CARD_VARIANT) {
    return <TableCard key={item._metadata?.uid} item={item} />;
  }
  if (variant === 'vertical') {
    return <VerticalProductCard key={item._metadata?.uid} item={item} />;
  }
  const { productItems } = parseItemBody(item.body);
  return productItems.length > 0 ? (
    <ProductCard key={item._metadata?.uid} item={item} />
  ) : (
    <LabelCard key={item._metadata?.uid} item={item} />
  );
};

// --- Layouts ---

const DefaultLayout: React.FC<{ cardGroups: CmsGroup[] }> = ({ cardGroups }) => (
  <>
    {cardGroups.map((group, groupIndex) => (
      <React.Fragment key={group._metadata?.uid ?? groupIndex}>
        <div className={groupIndex > 0 ? 'mt-150 self-stretch' : 'self-stretch'}>
          <GroupBanner title={group.group_title} className="mb-150" />
        </div>
        <div className="flex gap-150 self-stretch">
          {group.items.map((item) => renderCard(item, 'horizontal'))}
        </div>
      </React.Fragment>
    ))}
  </>
);

const ThreeGroupLayout: React.FC<{ cardGroups: CmsGroup[] }> = ({ cardGroups }) => {
  const totalCards = cardGroups.reduce((sum, g) => sum + g.items.length, 0);
  const gridStyle = { display: 'grid' as const, gridTemplateColumns: `repeat(${totalCards}, 1fr)` };

  return (
    <>
      <div className="self-stretch gap-[24px] mb-150" style={gridStyle}>
        {cardGroups.map((group) => (
          <div
            key={`banner-${group._metadata?.uid}`}
            style={{ gridColumn: `span ${group.items.length}` }}
          >
            <GroupBanner title={group.group_title} />
          </div>
        ))}
      </div>
      <div className="self-stretch gap-[24px]" style={gridStyle}>
        {cardGroups.flatMap((g) => g.items).map((item) => renderCard(item, 'vertical'))}
      </div>
    </>
  );
};

// --- Main component ---

export const CmsNoSearchResultsLinks: React.FC<CmsNoSearchResultsLinksProps> = ({ data }) => {
  const groups = data?.groups ?? [];
  const cardGroups = groups.filter((g) => g.items.length > 0);
  const emptyGroup = groups.find((g) => g.items.length === 0);
  const isThreeGroupLayout = groups.length >= 3;
  const hasTableCard = cardGroups.some((g) =>
    g.items.some((item) => item.url === TABLE_CARD_VARIANT)
  );

  return (
    <div
      className={`w-full flex flex-col items-center bg-gradient-to-b from-[#C0E1F5] to-white pt-250 px-500 ${emptyGroup ? 'pb-0' : 'pb-350'}`}
    >
      {data?.name && (
        <h2 className="text-center font-bold mb-150 w-full text-[#2253A0] text-heading-300 leading-heading tracking-[-1.44px]">
          {data.name}
        </h2>
      )}
      {data?.body && (
        <p
          className={`text-center font-medium mx-auto mb-150 text-[#34363B] text-body-125 leading-body ${hasTableCard ? 'max-w-[1248px]' : 'max-w-[1022px]'}`}
          style={LIGA_OFF}
        >
          {data.body}
        </p>
      )}
      {isThreeGroupLayout ? (
        <ThreeGroupLayout cardGroups={cardGroups} />
      ) : (
        <DefaultLayout cardGroups={cardGroups} />
      )}
      {emptyGroup && (
        <div className="mt-150 self-stretch">
          <GroupBanner
            title={emptyGroup.group_title}
            subtitle={emptyGroup.group_subtitle}
            compact
          />
        </div>
      )}
    </div>
  );
};

export default CmsNoSearchResultsLinks;
